import React from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import LtsInputText from '../../../components/lottus/lts-input-text'
import LtsInputNumber from '../../../components/lottus/lts-input-number'

/**
 * renderForm/validate da Edição Visual da definição de CRUD dinâmico
 * (plugados no LtsJsonEditorDialog via LtsJsonArea).
 *
 * Edita os campos de decisão humana da definição: títulos, seleção múltipla,
 * ordenação padrão, e por campo label/flags/tamanho/campoExibicao do lookup.
 * Estrutura (tabela, nome/tipo dos campos, bloco lookup) vem do assistente
 * "Obter Definição" ou do JSON — aqui é somente leitura; campo pode ser removido.
 */

const FLAGS = [
  { chave: 'listagem', label: 'Listagem', defaultTrue: false },
  { chave: 'formulario', label: 'Formulário', defaultTrue: true },
  { chave: 'filtro', label: 'Filtro', defaultTrue: false },
  { chave: 'ordenavel', label: 'Ordenável', defaultTrue: false },
  { chave: 'obrigatorio', label: 'Obrigatório', defaultTrue: false },
  { chave: 'editavel', label: 'Editável', defaultTrue: true },
]

const flagAtiva = (campo, flag) => (flag.defaultTrue ? campo[flag.chave] !== false : Boolean(campo[flag.chave]))

/**
 * Coluna de código da listagem (PK): não é campo declarado, mas pode ser a ordenação
 * padrão. O nome vem da ordenação já gravada (gerada pelo assistente) ou da convenção
 * oid_<tabela>; o BE valida contra a PK real da tabela ao salvar.
 */
const colunaCodigoDe = (data) => {
  const campos = (data && data.campos) || []
  const ordenacao = data && data.ordenacaoPadrao && data.ordenacaoPadrao[0]
  if (ordenacao && ordenacao.campo && !campos.some((campo) => campo.nome === ordenacao.campo)) {
    return ordenacao.campo
  }
  return data && data.tabela ? `oid_${data.tabela}` : null
}

export const validateDefinicaoVisual = (data) => {
  const errors = {}
  if (!data || !String(data.tituloForm || '').trim()) {
    errors.tituloForm = 'Informe o título do formulário'
  }
  if (!data || !String(data.tituloListagem || '').trim()) {
    errors.tituloListagem = 'Informe o título da listagem'
  }
  const campos = (data && data.campos) || []
  if (!campos.length) {
    errors.campos = 'A definição precisa declarar ao menos um campo'
  }
  campos.forEach((campo, index) => {
    if (!String(campo.label || '').trim()) {
      errors[`campo_${index}_label`] = 'Informe o label'
    }
    const formulario = campo.formulario !== false
    if (!formulario && !campo.listagem) {
      errors[`campo_${index}_flags`] = 'Campo precisa estar na listagem ou no formulário'
    }
    if (!formulario && campo.obrigatorio) {
      errors[`campo_${index}_flags`] = 'Campo obrigatório precisa estar no formulário'
    }
    if (campo.tipo === 'lookup' && !String((campo.lookup && campo.lookup.campoExibicao) || '').trim()) {
      errors[`campo_${index}_exibicao`] = 'Informe o campo de exibição do lookup'
    }
  })
  const ordenacao = data && data.ordenacaoPadrao && data.ordenacaoPadrao[0]
  if (
    ordenacao &&
    ordenacao.campo &&
    ordenacao.campo !== colunaCodigoDe(data) &&
    !campos.some((campo) => campo.nome === ordenacao.campo)
  ) {
    errors.ordenacaoPadrao = `Ordenação padrão referencia campo removido: ${ordenacao.campo}`
  }
  return Object.keys(errors).length ? errors : null
}

export const renderDefinicaoVisualForm = (data, onChange, errors) => {
  const campos = data.campos || []

  const atualizarCampo = (index, mudanca) => {
    onChange(
      'campos',
      campos.map((campo, i) => (i === index ? { ...campo, ...mudanca } : campo))
    )
  }

  const removerCampo = (index) => {
    onChange(
      'campos',
      campos.filter((_, i) => i !== index)
    )
  }

  const ordenacao = (data.ordenacaoPadrao && data.ordenacaoPadrao[0]) || {}
  const colunaCodigo = colunaCodigoDe(data)
  const opcoesOrdenacao = [
    ...(colunaCodigo ? [{ label: `${colunaCodigo} (código)`, value: colunaCodigo }] : []),
    ...campos.map((campo) => ({ label: campo.nome, value: campo.nome })),
  ]

  const setOrdenacao = (campo, ordem) => {
    // código (PK) ordena decrescente por padrão — registro mais novo primeiro
    const padrao = campo === colunaCodigo ? 'DESC' : 'ASC'
    onChange('ordenacaoPadrao', campo ? [{ campo, ordem: ordem || padrao }] : [])
  }

  const erro = (chave) => errors && errors[chave] && <small className="p-error">{errors[chave]}</small>

  return (
    <div className="p-col-12">
      <div className="p-grid form-group">
        <div className="p-col-12 p-md-4">
          <LtsInputText value={data.tabela} name="tabela" label="Tabela" disabled={true} onChange={() => {}} />
        </div>
        <div className="p-col-12 p-md-4">
          <LtsInputText
            value={data.tituloForm}
            name="tituloForm"
            label="Título do formulário"
            onChange={(valor) => onChange('tituloForm', valor)}
          />
          {erro('tituloForm')}
        </div>
        <div className="p-col-12 p-md-4">
          <LtsInputText
            value={data.tituloListagem}
            name="tituloListagem"
            label="Título da listagem"
            onChange={(valor) => onChange('tituloListagem', valor)}
          />
          {erro('tituloListagem')}
        </div>
      </div>
      <div className="p-grid form-group">
        <div className="p-col-12 p-md-4">
          <Checkbox
            inputId="chkSelecaoMultipla"
            checked={data.selecaoMultipla !== false}
            onChange={(e) => onChange('selecaoMultipla', e.checked)}
            style={{ marginRight: '5px' }}
          />
          <label htmlFor="chkSelecaoMultipla" className="p-checkbox-label">
            Seleção múltipla na listagem
          </label>
        </div>
        <div className="p-col-8 p-md-4">
          <Dropdown
            value={ordenacao.campo || null}
            options={opcoesOrdenacao}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setOrdenacao(e.value, e.value === ordenacao.campo ? ordenacao.ordem : undefined)}
            placeholder="Ordenação padrão"
            showClear
            style={{ width: '100%' }}
          />
        </div>
        <div className="p-col-4 p-md-2">
          <Dropdown
            value={(ordenacao.ordem || 'ASC').toUpperCase()}
            options={[
              { label: 'Crescente', value: 'ASC' },
              { label: 'Decrescente', value: 'DESC' },
            ]}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setOrdenacao(ordenacao.campo, e.value)}
            disabled={!ordenacao.campo}
            style={{ width: '100%' }}
          />
        </div>
        <div className="p-col-12">{erro('ordenacaoPadrao')}</div>
      </div>

      <h3 style={{ margin: '10px 0' }}>Campos</h3>
      {erro('campos')}
      {campos.map((campo, index) => (
        <div
          key={campo.nome}
          style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px 10px', marginBottom: '8px' }}
        >
          <div className="p-grid form-group" style={{ alignItems: 'flex-end' }}>
            <div className="p-col-12 p-md-4">
              <strong>{campo.nome}</strong>{' '}
              <small style={{ color: '#888' }}>
                ({campo.tipo}
                {campo.tipo === 'lookup' && campo.lookup ? ` → ${campo.lookup.tabela}` : ''})
              </small>
            </div>
            <div className="p-col-10 p-md-4">
              <LtsInputText
                value={campo.label}
                name={`label_${campo.nome}`}
                label="Label"
                onChange={(valor) => atualizarCampo(index, { label: valor })}
              />
              {erro(`campo_${index}_label`)}
            </div>
            {campo.tipo === 'string' && (
              <div className="p-col-6 p-md-2">
                <LtsInputNumber
                  value={campo.tamanho}
                  name={`tamanho_${campo.nome}`}
                  label="Tamanho"
                  onChange={(valor) => atualizarCampo(index, { tamanho: valor || undefined })}
                />
                {(!campo.tamanho || campo.tamanho > 255) && (
                  <small style={{ color: '#888' }}>Acima de 255 (ou vazio): editado em área de texto</small>
                )}
              </div>
            )}
            {campo.tipo === 'lookup' && (
              <div className="p-col-6 p-md-2">
                <LtsInputText
                  value={campo.lookup && campo.lookup.campoExibicao}
                  name={`exibicao_${campo.nome}`}
                  label="Campo de exibição"
                  onChange={(valor) => atualizarCampo(index, { lookup: { ...campo.lookup, campoExibicao: valor } })}
                />
                {erro(`campo_${index}_exibicao`)}
              </div>
            )}
            <div className="p-col-2 p-md-2" style={{ textAlign: 'right' }}>
              <Button
                icon="pi pi-trash"
                className="p-button-danger p-button-outlined p-button-sm"
                tooltip="Remover campo"
                onClick={() => removerCampo(index)}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            {FLAGS.map((flag) => (
              <span key={flag.chave}>
                <Checkbox
                  inputId={`chk_${flag.chave}_${campo.nome}`}
                  checked={flagAtiva(campo, flag)}
                  onChange={(e) => atualizarCampo(index, { [flag.chave]: e.checked })}
                  style={{ marginRight: '5px' }}
                />
                <label htmlFor={`chk_${flag.chave}_${campo.nome}`} className="p-checkbox-label">
                  {flag.label}
                </label>
              </span>
            ))}
          </div>
          {erro(`campo_${index}_flags`)}
        </div>
      ))}
    </div>
  )
}
